import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Container, Row, Col, Card, CardHeader, CardBody, Table, Input } from "reactstrap";
import CustomAudioPlayer from "../components/AudioPlayer/CustomAudioPlayer";
import SpeciesCard from "../components/AudioPlayer/SpeciesCard";
import WeeklyMigrationChart from "../components/AudioPlayer/WeeklyMigrationChart";
import Header from "components/Headers/Header.js";

const API_BASE = process.env.REACT_APP_API_URL || "/api";

const Species = () => {
  const { name } = useParams(); // pulled from /species/:name
  const [speciesData, setSpeciesData] = useState(null);
  const [speciesList, setSpeciesList] = useState([]);
  const [speciesStats, setSpeciesStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchFilter, setSearchFilter] = useState("");

  useEffect(() => {
    const fetchSpecies = async () => {
      setLoading(true);
      try {
        if (name) {
          // no fetch needed, child components will handle it
          console.log("!bang Species view mounted with name:", name);
        } else {
          console.log("!bang Species view in list mode");
          const [listRes, statsRes] = await Promise.all([
            fetch(`${API_BASE}/detections/specieslist`),
            fetch(`${API_BASE}/detections/speciesstats`)
          ]);
          const listData = await listRes.json();
          const statsData = await statsRes.json();
          setSpeciesList(listData);
          setSpeciesStats(statsData);
        }
      } catch (err) {
        console.error("Error fetching species info:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchSpecies();
  }, [name]);

  const [playersReady, setPlayersReady] = useState(0);

  useEffect(() => {
    console.log("!bang Players ready count:", playersReady);
    if (playersReady === 2) {
      CustomAudioPlayer.initAllPlayers();
    }
  }, [playersReady]);

  if (loading) return <Container><p>Loading...</p></Container>;

  // 🔁 List mode fallback
  if (!name) {
    console.log("Species list data:", speciesList);

    // Filter species based on search term
    const filteredSpecies = speciesList.filter((s) => {
      const searchLower = searchFilter.toLowerCase();
      return (
        s.Com_Name.toLowerCase().includes(searchLower) ||
        s.Sci_Name.toLowerCase().includes(searchLower)
      );
    });

    return (
      <>
        <Header />
        <Container className="mt--7" fluid>
          {/* Stats Header */}
          {speciesStats && (
            <Row className="mb-4">
              <Col md="12">
                <Card className="shadow" style={{ background: "linear-gradient(87deg, #5e72e4 0, #825ee4 100%)" }}>
                  <CardBody>
                    <div style={{ color: "white" }}>
                      <h3 style={{ color: "white", marginBottom: "20px", fontWeight: "600" }}>
                        Species Overview
                      </h3>
                      <Row>
                        <Col md="4">
                          <div style={{ marginBottom: "16px" }}>
                            <div style={{ fontSize: "2.5rem", fontWeight: "700", lineHeight: "1.2" }}>
                              {speciesStats.totalSpecies}
                            </div>
                            <div style={{ fontSize: "0.875rem", opacity: "0.8", marginTop: "4px" }}>
                              Unique Species Detected
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: "1.5rem", fontWeight: "600", lineHeight: "1.2" }}>
                              {speciesStats.totalDetections.toLocaleString()}
                            </div>
                            <div style={{ fontSize: "0.875rem", opacity: "0.8", marginTop: "4px" }}>
                              Total Detections
                            </div>
                          </div>
                        </Col>
                        <Col md="8">
                          <h5 style={{ color: "white", marginBottom: "12px", fontWeight: "600", opacity: "0.9" }}>
                            Most Detected Species
                          </h5>
                          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                            {speciesStats.topSpecies.map((species, idx) => (
                              <div
                                key={idx}
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                                  padding: "10px 16px",
                                  borderRadius: "6px",
                                  backdropFilter: "blur(10px)"
                                }}
                              >
                                <div style={{ flex: 1 }}>
                                  <a
                                    href={`/admin/species/${encodeURIComponent(species.Com_Name)}`}
                                    style={{ color: "white", fontWeight: "600", fontSize: "1rem", textDecoration: "none" }}
                                  >
                                    {idx + 1}. {species.Com_Name}
                                  </a>
                                  <div style={{ fontSize: "0.75rem", opacity: "0.7", fontStyle: "italic" }}>
                                    {species.Sci_Name}
                                  </div>
                                </div>
                                <div style={{ textAlign: "right", marginLeft: "16px" }}>
                                  <div style={{ fontSize: "1.25rem", fontWeight: "700" }}>
                                    {species.totalDetections.toLocaleString()}
                                  </div>
                                  <div style={{ fontSize: "0.7rem", opacity: "0.7" }}>detections</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}

          {/* Species List */}
          <Row>
            <Col md="12">
              <Card className="shadow">
                <CardHeader>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
                    <h3 style={{ margin: 0 }}>All Species Detected</h3>
                    <Input
                      type="text"
                      placeholder="Search species..."
                      value={searchFilter}
                      onChange={(e) => setSearchFilter(e.target.value)}
                      style={{ maxWidth: "300px", margin: 0 }}
                    />
                  </div>
                  {searchFilter && (
                    <div style={{ marginTop: "8px", fontSize: "0.875rem", color: "#8898aa" }}>
                      Showing {filteredSpecies.length} of {speciesList.length} species
                    </div>
                  )}
                </CardHeader>
                <CardBody>
                  <Table responsive>
                    <thead className="thead-light">
                      <tr>
                        <th>Common Name</th>
                        <th>Scientific Name</th>
                        <th>Total Detections</th>
                        <th>Last Detection</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSpecies.map((s, i) => (
                        <tr key={i}>
                          <td>
                            <a href={`/admin/species/${encodeURIComponent(s.Com_Name)}`}>
                              {s.Com_Name}
                            </a>
                          </td>
                          <td><i>{s.Sci_Name}</i></td>
                          <td>{s.totalDetections.toLocaleString()}</td>
                          <td>{s.lastDetected}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  {filteredSpecies.length === 0 && searchFilter && (
                    <div style={{ textAlign: "center", padding: "20px", color: "#8898aa" }}>
                      No species found matching "{searchFilter}"
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </>
    );
  }

  // 🎯 Detailed mode
  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <Row>
          <Col md="12"><SpeciesCard species={name} /></Col>
        </Row>
        <Row className="mt-4">
          <Col md="12">
            <Card className="shadow">
              <CardBody>
                <WeeklyMigrationChart species={name} />
              </CardBody>
            </Card>
          </Col>
        </Row>
        <Row className="mt-4">
          <Col md="6">
            <Card className="shadow">
              <CardHeader><h4>Latest Detection</h4></CardHeader>
              <CardBody>
                <CustomAudioPlayer species={name} mode="latest" onReady={() => setPlayersReady(prev => prev + 1)} />
              </CardBody>
            </Card>
          </Col>
          <Col md="6">
            <Card className="shadow">
              <CardHeader><h4>Best Detection</h4></CardHeader>
              <CardBody>
                <CustomAudioPlayer species={name} mode="best" onReady={() => setPlayersReady(prev => prev + 1)}/>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default Species;
